/* Nividas PDP gallery — May 2026.
   Two-up desktop / one-up mobile horizontal scroll-snap carousel with a
   sliding-indicator pager. Reads cellsPerView from layout, so one piece of
   JS handles both breakpoints. Re-evaluates on resize. */

(function () {
  'use strict';

  function initGallery(root) {
    if (!root || root.dataset.nvBound === '1') return;
    root.dataset.nvBound = '1';

    const track     = root.querySelector('[data-nv-pdp-gallery-track]');
    const pager     = root.querySelector('[data-nv-pdp-gallery-pager]');
    const indicator = root.querySelector('[data-nv-pdp-gallery-indicator]');
    const btnPrev   = root.querySelector('[data-nv-pdp-gallery-prev]');
    const btnNext   = root.querySelector('[data-nv-pdp-gallery-next]');
    if (!track || !pager || !indicator) return;

    let cells       = [];
    let cellWidth   = 0;
    let perView     = 1;
    let pageCount   = 0;
    let active      = 0;
    let bars        = [];
    let raf         = 0;

    // Visible cells = those not display:none. The CSS hides model cells on
    // mobile, so this filter is what differentiates the two breakpoints.
    function readCells() {
      cells = Array.from(track.querySelectorAll('[data-nv-pdp-gallery-cell]'))
        .filter(c => c.offsetParent !== null || c.getClientRects().length > 0);
    }

    function measure() {
      readCells();
      if (!cells.length) return;
      cellWidth = cells[0].getBoundingClientRect().width;
      const viewportW = track.clientWidth;
      perView   = Math.max(1, Math.round(viewportW / Math.max(1, cellWidth)));
      pageCount = Math.max(1, cells.length - perView + 1);
    }

    function buildPager() {
      // Remove any prior bars but keep the indicator span.
      Array.from(pager.querySelectorAll('.nv-pdp-gallery__pager-btn')).forEach(b => b.remove());
      bars = [];
      for (let i = 0; i < pageCount; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'nv-pdp-gallery__pager-btn';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-label', `Page ${i + 1} of ${pageCount}`);
        b.addEventListener('click', () => goTo(i));
        // Insert before the indicator so flex order is bar1, bar2, ..., indicator.
        pager.insertBefore(b, indicator);
        bars.push(b);
      }
    }

    function positionIndicator() {
      const bar = bars[active];
      if (!bar) return;
      indicator.style.transform = `translateX(${bar.offsetLeft}px)`;
      indicator.style.width = `${bar.offsetWidth}px`;
    }

    function goTo(idx) {
      const clamped = Math.max(0, Math.min(pageCount - 1, idx));
      active = clamped;
      track.scrollTo({ left: clamped * cellWidth, behavior: 'smooth' });
      positionIndicator();
      bars.forEach((b, i) => b.setAttribute('aria-selected', String(i === clamped)));
      updateArrows();
      eagerLoadNearby(clamped);
    }

    function updateArrows() {
      if (btnPrev) btnPrev.disabled = active <= 0;
      if (btnNext) btnNext.disabled = active >= pageCount - 1;
    }

    // Promote nearby cells from lazy → eager so by the time the user swipes
    // to them, the image is already fetched (or fetching) — no visible lag.
    // Window of N cells in each direction. ±2 is enough for one-up mobile;
    // covers the next two-up pair on desktop too.
    function eagerLoadNearby(centerIndex) {
      if (!cells.length) return;
      const radius = 2;
      const lo = Math.max(0, centerIndex - radius);
      const hi = Math.min(cells.length - 1, centerIndex + radius);
      for (let i = lo; i <= hi; i++) {
        const img = cells[i] && cells[i].querySelector('img');
        if (img && img.loading === 'lazy') img.loading = 'eager';
      }
    }

    function syncFromScroll() {
      if (!cellWidth) return;
      const i = Math.round(track.scrollLeft / cellWidth);
      const clamped = Math.max(0, Math.min(pageCount - 1, i));
      if (clamped !== active) {
        active = clamped;
        positionIndicator();
        bars.forEach((b, j) => b.setAttribute('aria-selected', String(j === clamped)));
        updateArrows();
        eagerLoadNearby(clamped);
      }
    }

    function rebuild() {
      measure();
      buildPager();
      // Re-clamp active to new pageCount; keep current scroll position.
      active = Math.min(active, pageCount - 1);
      requestAnimationFrame(positionIndicator);
      updateArrows();
      // Preload neighbours of the active cell on first paint.
      eagerLoadNearby(active);
    }

    function onCellClick(e) {
      const cell = e.target.closest('[data-nv-pdp-gallery-cell]');
      if (!cell) return;
      // Only desktop two-up has prev/next click semantics — on mobile (perView=1)
      // a click on the only-visible cell would just re-scroll to itself, so skip.
      if (perView < 2) return;
      const idx = parseInt(cell.dataset.index || '0', 10);
      if (idx === active) {
        goTo(active - 1);
      } else if (idx === active + 1) {
        goTo(active + 1);
      } else if (idx > active + 1) {
        goTo(idx - 1);
      } else {
        goTo(idx);
      }
    }

    function onKeyDown(e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(active + 1); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); goTo(active - 1); }
    }

    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(syncFromScroll);
    }, { passive: true });

    track.addEventListener('click', onCellClick);
    root.addEventListener('keydown', onKeyDown);
    if (btnPrev) btnPrev.addEventListener('click', () => goTo(active - 1));
    if (btnNext) btnNext.addEventListener('click', () => goTo(active + 1));

    const ro = new ResizeObserver(() => rebuild());
    ro.observe(track);

    // The Rx lens toggle shows and hides whole cells. That changes which
    // cells are visible without changing the track's size, so the
    // ResizeObserver never fires and the pager would keep the old page
    // count. Re-measure on request instead.
    document.addEventListener('nv:gallery:refresh', function () {
      track.scrollLeft = 0;
      active = 0;
      rebuild();
    });

    // Initial setup. Wait one rAF so layout has settled (matters when the
    // section is rendered inside a hidden tab or modal, then revealed).
    requestAnimationFrame(() => requestAnimationFrame(rebuild));
  }

  function init() {
    document.querySelectorAll('[data-nv-pdp-gallery]').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init if Shopify section is reloaded in the editor.
  document.addEventListener('shopify:section:load', init);
})();
